import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { MAX_QUOTE_LOST_REASON_LENGTH, QUOTE_HUMAN_TRANSITIONS } from "@school-sales-ai/shared";
import { PageHeader } from "@/components/PageHeader";
import { QuoteStatusBadge } from "@/components/quotes/quote-status";
import { useQuote, useSetQuoteStatus } from "@/hooks/useQuotes";
import { QUOTE_ACTION_LABEL } from "@/lib/quote-status-meta";

function formatDateTime(iso: string | null): string | null {
  if (!iso) return null;
  return new Date(iso).toLocaleString("es-MX", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * One quote request: what the customer needs, who to send the price to, and the
 * team's moves on it. The pricing itself happens outside the system, so this
 * screen records the outcome (taken, sent, won, lost) and never asks for an amount.
 */
export function CotizacionDetalle(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: quote, isLoading } = useQuote(id ?? "");
  const setStatus = useSetQuoteStatus();
  const [losing, setLosing] = useState(false);
  const [lostReason, setLostReason] = useState("");

  if (isLoading || !quote) {
    return (
      <div className="flex h-full flex-col">
        <PageHeader title="Cotización" onBack={() => navigate("/cotizaciones")} />
        <p className="px-6 py-8 text-sm text-ink-500">
          {isLoading ? "Cargando solicitud…" : "Solicitud no encontrada."}
        </p>
      </div>
    );
  }

  // The moves come from the SHARED table the endpoint validates against, so a
  // button here and a refusal there cannot disagree.
  const moves = QUOTE_HUMAN_TRANSITIONS[quote.status].filter((s) => s !== "lost");
  const canLose = QUOTE_HUMAN_TRANSITIONS[quote.status].includes("lost");
  const hasChangedLines = quote.lines.some((l) => l.changedAfterSubmit);

  const dates = [
    ["Recibida", formatDateTime(quote.submittedAt)],
    ["Último aviso al equipo", quote.notifiedAt !== quote.submittedAt ? formatDateTime(quote.notifiedAt) : null],
    ["Enviada", formatDateTime(quote.sentAt)],
    ["Cerrada", formatDateTime(quote.closedAt)],
  ].filter((d): d is [string, string] => d[1] !== null);

  return (
    <div className="flex h-full flex-col">
      <PageHeader title={`Cotización #${quote.number}`} onBack={() => navigate("/cotizaciones")} />

      <div className="flex-1 overflow-y-auto px-4 py-4 md:px-6 md:py-5">
        <div className="mb-5 flex flex-wrap items-center gap-3">
          <QuoteStatusBadge status={quote.status} />
          <Link to={`/leads/${quote.leadId}`} className="text-sm font-medium text-ink-900 underline">
            {quote.leadName ?? "Sin nombre"}
          </Link>
          <span className="font-mono text-[11px] text-ink-500">{quote.sessionRef}</span>
          {quote.leadPhone && <span className="text-sm text-ink-600">{quote.leadPhone}</span>}
          {quote.leadEmail && <span className="text-sm text-ink-600">{quote.leadEmail}</span>}
        </div>

        {quote.complementsId && quote.complementsNumber !== null && (
          <p className="mb-4 text-sm text-ink-600">
            Complementa la{" "}
            <Link to={`/cotizaciones/${quote.complementsId}`} className="font-medium underline">
              solicitud #{quote.complementsNumber}
            </Link>
            : el cliente agregó esto cuando el equipo ya la estaba cotizando.
          </p>
        )}

        {dates.length > 0 && (
          <div className="mb-5 flex flex-wrap gap-x-6 gap-y-1 text-[12px] text-ink-500">
            {dates.map(([label, value]) => (
              <span key={label}>
                {label}: <span className="text-ink-700">{value}</span>
              </span>
            ))}
          </div>
        )}

        <div className="mb-5 overflow-x-auto rounded-lg border border-ink-100">
          <table className="w-full min-w-[36rem] text-sm">
            <thead>
              <tr className="border-b border-ink-100 text-left text-xs uppercase text-ink-500">
                <th className="px-4 py-2">#</th>
                <th className="px-4 py-2">Producto</th>
                <th className="px-4 py-2">Referencia</th>
                <th className="px-4 py-2 text-right">Cantidad</th>
                <th className="px-4 py-2">Notas</th>
              </tr>
            </thead>
            <tbody>
              {quote.lines.map((l) => (
                <tr key={l.id} className="border-b border-ink-50 last:border-0">
                  <td className="px-4 py-2 text-ink-500">{l.position}</td>
                  <td className="px-4 py-2 text-ink-900">
                    {l.productName}
                    {l.changedAfterSubmit && (
                      <span className="ml-2 rounded-full bg-lime/20 px-1.5 py-0.5 text-[10.5px] font-medium text-moss">
                        Cambió después del envío
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2 font-mono text-[12.5px] text-ink-700">
                    {l.requestedReference ?? <span className="font-sans text-ink-400">—</span>}
                  </td>
                  <td className="px-4 py-2 text-right text-ink-900">{l.quantity}</td>
                  <td className="px-4 py-2 text-ink-600">{l.notes ?? ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {hasChangedLines && quote.status === "submitted" && (
          <p className="mb-5 text-[12px] text-ink-500">
            El cliente cambió esta solicitud después de enviarla. Mientras nadie la tome, el
            agente puede seguir ajustándola.
          </p>
        )}

        {quote.lostReason && (
          <p className="mb-5 text-sm text-ink-600">
            Razón: <span className="text-ink-900">{quote.lostReason}</span>
          </p>
        )}

        {(moves.length > 0 || canLose) && (
          <div className="flex flex-col gap-3 border-t border-ink-100 pt-4">
            <div className="flex flex-wrap gap-2">
              {moves.map((to, i) => (
                <button
                  key={to}
                  type="button"
                  disabled={setStatus.isPending}
                  onClick={() => setStatus.mutate({ id: quote.id, status: to })}
                  className={
                    i === 0
                      ? "rounded-lg bg-lime px-4 py-2 text-sm font-semibold text-moss hover:bg-lime-dk disabled:opacity-40"
                      : "rounded-lg border border-ink-200 px-4 py-2 text-sm font-medium text-ink-700 hover:bg-ink-050 disabled:opacity-40"
                  }
                >
                  {QUOTE_ACTION_LABEL[to] ?? to}
                </button>
              ))}
              {canLose && !losing && (
                <button
                  type="button"
                  onClick={() => setLosing(true)}
                  className="rounded-lg border border-red-200 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50"
                >
                  {QUOTE_ACTION_LABEL.lost}
                </button>
              )}
            </div>
            {quote.status === "submitted" && (
              <p className="text-[12px] text-ink-500">
                Al tomarla, el agente deja de modificarla: lo que el cliente agregue después llega
                como una solicitud nueva que la complementa.
              </p>
            )}
            {losing && (
              <div className="flex flex-col gap-2">
                <textarea
                  value={lostReason}
                  onChange={(e) => setLostReason(e.target.value)}
                  maxLength={MAX_QUOTE_LOST_REASON_LENGTH}
                  rows={2}
                  aria-label="Razón"
                  placeholder="¿Por qué se perdió? (opcional)"
                  className="w-full rounded-lg border border-ink-200 px-3 py-2 text-sm text-ink-900 placeholder:text-ink-300 focus:border-moss focus:outline-none"
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={setStatus.isPending}
                    onClick={() =>
                      setStatus.mutate(
                        { id: quote.id, status: "lost", lostReason: lostReason.trim() || null },
                        { onSuccess: () => setLosing(false) },
                      )
                    }
                    className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-40"
                  >
                    Marcar como perdida
                  </button>
                  <button
                    type="button"
                    onClick={() => setLosing(false)}
                    className="rounded-lg border border-ink-200 px-3 py-1.5 text-xs font-medium text-ink-700"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
